import { Box, Typography, Stack, Button, CircularProgress, Alert, Chip, IconButton, Sheet, LinearProgress, Checkbox, Modal, ModalDialog, ModalClose, Input } from "@mui/joy";
import { useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LayoutContext } from "../context";
import { APIGetRoadmaps, APIEnrollRoadmap, APIGetPlanModuleDetail, APICompleteTask, APIUncompleteTask, APIStartPlanTest, APISearchModules, APILinkTaskModules, APITogglePlanModule, APIToggleFreeMode, APIUpdateDay, APIDeletePlanTask, APIUpdateTask, APIAddTask, detectTrialLocked } from "../api";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router";
import { CheckCircle, Circle, Lock, BookOpen, Exam, PlayCircle, Cards, ArrowCounterClockwise, ClipboardText, Trophy, ChatDots, CaretDown, Play, ArrowLeft, SquaresFour, List, Table, PencilSimple, X, Trash, Plus, Confetti, Question } from "@phosphor-icons/react";
import DemoExplainerModal from "../components/OnboardingTour/DemoExplainerModal";
import OnboardingTour from "../components/OnboardingTour/OnboardingTour";
import { LS_KEYS } from "../components/OnboardingTour/tourSteps";
import AccessInfoBanner from "../components/ui/AccessInfoBanner";


// ─── Tour target computation ───
// Scan a day's tasks and return the task IDs that should receive
// data-tour-id attributes for the post-onboarding guided tour.
// For read tasks, if the first read task has subtasks (a parent container),
// we dive into its first subtask so the tour spotlights the actual clickable
// module link instead of the empty parent row.
function computeTourTargets(tasks) {
    let firstReadTaskId = null;
    let firstQuizTaskId = null;
    let firstGateTaskId = null;
    for (const t of tasks || []) {
        if (firstReadTaskId == null && t.task_type === 'read') {
            if (t.subtasks && t.subtasks.length > 0) {
                firstReadTaskId = t.subtasks[0].id;
            } else {
                firstReadTaskId = t.id;
            }
        }
        if (firstQuizTaskId == null && (t.task_type === 'mini_quiz' || t.task_type === 'qbank')) {
            firstQuizTaskId = t.id;
        }
        if (firstGateTaskId == null && (t.task_type === 'gate' || t.task_type === 'final_gate')) {
            firstGateTaskId = t.id;
        }
    }
    return { firstReadTaskId, firstQuizTaskId, firstGateTaskId };
}

function getTourIdForTask(taskId, targets) {
    if (!targets) return null;
    if (taskId === targets.firstReadTaskId) return 'first-read-task';
    if (taskId === targets.firstQuizTaskId) return 'first-quiz-task';
    if (taskId === targets.firstGateTaskId) return 'first-gate-task';
    return null;
}


// Uniform neutral gray for all task type icons — form distinguishes type, color distinguishes state
const ICON_NEUTRAL = '#9CA3AF';

const TASK_ICONS = {
    read: BookOpen,
    mini_quiz: Exam,
    video: PlayCircle,
    anki_new: Cards,
    anki_review: ArrowCounterClockwise,
    qbank: ClipboardText,
    gate: Lock,
    final_gate: Trophy,
    review: ChatDots,
    onboarding: BookOpen,
    other: Circle,
};

const STATE = {
    completed: '#788C5D',
    active: '#DA7756',
    locked: '#9CA3AF',
};

const getScoreStyle = (score) => {
    if (score >= 72) return { color: '#5a7a48', bgcolor: 'rgba(90,122,72,0.12)', borderColor: 'rgba(90,122,72,0.25)' };
    if (score >= 61) return { color: '#B8860B', bgcolor: 'rgba(184,134,11,0.10)', borderColor: 'rgba(184,134,11,0.25)' };
    return { color: '#C4554D', bgcolor: 'rgba(196,85,77,0.10)', borderColor: 'rgba(196,85,77,0.25)' };
};


// ─── Link Modules Modal (admin only) ───
function LinkModulesModal({ open, onClose, task, token, onSave }) {
    const [selected, setSelected] = useState([]);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (open && task) {
            setSelected(task.modules?.map(m => ({ id: m.id, title: m.title })) || []);
            setQuery('');
            setResults([]);
        }
    }, [open, task]);

    const handleSearch = (value) => {
        setQuery(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (value.trim().length < 2) { setResults([]); return; }
        debounceRef.current = setTimeout(async () => {
            setSearching(true);
            try {
                const res = await APISearchModules(token, value.trim());
                if (res.success) setResults(res.results);
            } catch (e) { console.error(e); }
            finally { setSearching(false); }
        }, 300);
    };

    const addModule = (mod) => {
        if (!selected.find(s => s.id === mod.id)) {
            setSelected(prev => [...prev, { id: mod.id, title: mod.title }]);
        }
    };

    const removeModule = (modId) => {
        setSelected(prev => prev.filter(s => s.id !== modId));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await APILinkTaskModules(token, task.id, selected.map(s => s.id));
            onSave();
            onClose();
        } catch (e) { console.error(e); }
        finally { setSaving(false); }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ maxWidth: 520, width: '90vw', p: 3 }}>
                <ModalClose />
                <Typography level="title-lg" sx={{ mb: 0.5 }}>Привязка модулей</Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                    {task?.title}
                </Typography>

                {/* Current modules as chips */}
                {selected.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {selected.map(m => (
                            <Chip key={m.id} size="sm" variant="soft" color="primary"
                                endDecorator={
                                    <Box component="span" sx={{ cursor: 'pointer', ml: 0.5, fontWeight: 700, fontSize: '0.85rem' }}
                                        onClick={() => removeModule(m.id)}>×</Box>
                                }>
                                {m.title}
                            </Chip>
                        ))}
                    </Box>
                )}

                {/* Search input */}
                <Input
                    placeholder="Поиск модулей..."
                    value={query}
                    onChange={(e) => handleSearch(e.target.value)}
                    size="sm"
                    sx={{ mb: 1 }}
                    endDecorator={searching ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '16px' }} /> : null}
                />

                {/* Results */}
                {results.length > 0 && (
                    <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
                        {results.filter(r => !selected.find(s => s.id === r.id)).map(r => (
                            <Box key={r.id} onClick={() => addModule(r)}
                                sx={{
                                    py: 0.75, px: 1.5, cursor: 'pointer',
                                    '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
                                    borderBottom: '1px solid', borderColor: 'divider',
                                    '&:last-child': { borderBottom: 'none' },
                                }}>
                                <Typography level="body-sm" sx={{ fontWeight: 500 }}>{r.title}</Typography>
                                {r.category_name && (
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{r.category_name}</Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}

                <Button onClick={handleSave} loading={saving} sx={{ mt: 2, bgcolor: '#B84C31', color: '#fff', '&:hover': { bgcolor: '#9A3F28' } }}>
                    Сохранить
                </Button>
            </ModalDialog>
        </Modal>
    );
}


// ─── Edit Task Modal (admin only — for video tasks and add new) ───
function EditTaskModal({ open, onClose, task, dayId, token, onSave }) {
    const isEdit = !!task;
    const [title, setTitle] = useState('');
    const [taskType, setTaskType] = useState('read');
    const [videoUrl, setVideoUrl] = useState('');
    const [timeMinutes, setTimeMinutes] = useState(0);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setTitle(task?.title || '');
            setTaskType(task?.task_type || 'read');
            setVideoUrl(task?.video_url || '');
            setTimeMinutes(task?.time_minutes || 0);
        }
    }, [open, task]);

    const isVideo = isEdit ? task?.task_type === 'video' : taskType === 'video';

    const handleSave = async () => {
        if (!title.trim()) return;
        setSaving(true);
        try {
            if (isEdit) {
                await APIUpdateTask(token, task.id, { title: title.trim(), ...(isVideo && { video_url: videoUrl.trim() }) });
            } else {
                await APIAddTask(token, dayId, {
                    title: title.trim(), task_type: taskType,
                    ...(taskType === 'video' && { video_url: videoUrl.trim() }),
                    time_minutes: Number(timeMinutes) || (taskType === 'read' ? 15 : 0),
                });
            }
            onSave();
            onClose();
        } catch (e) { console.error(e); }
        finally { setSaving(false); }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ maxWidth: 460, width: '90vw', p: 3 }}>
                <ModalClose />
                <Typography level="title-lg" sx={{ mb: 2 }}>
                    {isEdit ? 'Редактировать задачу' : 'Добавить задачу'}
                </Typography>
                <Stack spacing={1.5}>
                    {!isEdit && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {['read', 'video'].map(t => (
                                <Button key={t} size="sm" variant={taskType === t ? 'solid' : 'outlined'}
                                    onClick={() => setTaskType(t)}
                                    sx={taskType === t ? { bgcolor: '#B84C31', color: '#fff' } : { color: 'text.secondary' }}>
                                    {t === 'read' ? 'Чтение' : 'Видео'}
                                </Button>
                            ))}
                        </Box>
                    )}
                    <Box>
                        <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>Название</Typography>
                        <Input value={title} onChange={(e) => setTitle(e.target.value)}
                            placeholder={isVideo ? 'Например: Glycolysis overview' : 'Например: Collagen synthesis'} size="sm" />
                    </Box>
                    {isVideo && (
                        <Box>
                            <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>Ссылка на видео</Typography>
                            <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://..." size="sm" />
                        </Box>
                    )}
                    {!isEdit && (
                        <Box>
                            <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>Время (мин)</Typography>
                            <Input type="number" value={timeMinutes} onChange={(e) => setTimeMinutes(e.target.value)} size="sm" slotProps={{ input: { min: 0 } }} />
                        </Box>
                    )}
                    {!isEdit && taskType === 'read' && (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                            Автоматически станет sub-item в группе «Прочитай текстовый модуль»
                        </Typography>
                    )}
                    <Button onClick={handleSave} loading={saving} disabled={!title.trim()}
                        sx={{ mt: 0.5, bgcolor: '#B84C31', color: '#fff', '&:hover': { bgcolor: '#9A3F28' } }}>
                        {isEdit ? 'Сохранить' : 'Добавить'}
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}


// ─── Sub-Task Item (list layout — for children of parent tasks) ───
function SubTaskItem({ task, parentOrder, index, isEnrolled, dayStatus, token, onRefresh, navigate, user, roadmapId, moduleId, tourTargets }) {
    const { openUpgradeModal } = useContext(LayoutContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [unlinking, setUnlinking] = useState(false);
    const isAdmin = user?.role === 'admin';
    const isVideoTask = task.task_type === 'video';
    const canEditTask = isAdmin && (task.task_type === 'read' || task.task_type === 'video');
    const tourId = getTourIdForTask(task.id, tourTargets);

    const handleUnlink = async () => {
        setUnlinking(true);
        try { await APILinkTaskModules(token, task.id, []); onRefresh(); }
        catch (e) { console.error(e); }
        finally { setUnlinking(false); }
    };

    const isLocked = dayStatus === 'locked';
    const canInteract = isEnrolled && dayStatus === 'active' && !task.is_completed;
    const canUncheck = isEnrolled && dayStatus === 'active' && task.is_completed;

    const handleToggle = async () => {
        if (!task.is_completed && !canInteract) return;
        if (task.is_completed && !canUncheck) return;
        setLoading(true);
        try {
            if (task.is_completed) await APIUncompleteTask(token, task.id);
            else await APICompleteTask(token, task.id);
            onRefresh();
        } catch (e) {
            const trialLock = detectTrialLocked(e);
            if (trialLock) { openUpgradeModal(trialLock.message); }
            else setError('Ошибка');
        }
        finally { setLoading(false); }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Удалить "${task.title}"?`)) return;
        try { await APIDeletePlanTask(token, task.id); onRefresh(); }
        catch (e) { console.error(e); }
    };

    const label = `${parentOrder}.${index + 1}`;

    return (
        <Box
            {...(tourId && { 'data-tour-id': tourId })}
            sx={{
            display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1,
            borderRadius: '6px', opacity: isLocked ? 0.5 : 1,
            bgcolor: (theme) => task.is_completed
                ? (theme.palette.mode === 'dark' ? '#282e25' : '#f2f4ef')
                : (theme.palette.mode === 'dark' ? '#2c2c2c' : 'transparent'),
            '&:hover': !isLocked ? {
                bgcolor: (theme) => task.is_completed
                    ? (theme.palette.mode === 'dark' ? '#2e3528' : '#edf0e8')
                    : (theme.palette.mode === 'dark' ? '#333' : '#EFECE4'),
            } : {},
        }}>
            <Checkbox checked={task.is_completed} disabled={loading || isLocked || (!canInteract && !canUncheck)}
                onChange={handleToggle} size="sm" sx={{ '& .MuiCheckbox-checkbox': { borderRadius: '50%', borderColor: (theme) => theme.palette.mode === 'dark' ? '#888' : '#999', borderWidth: '2px' } }} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography level="body-xs" sx={{
                    textDecoration: task.is_completed ? 'line-through' : 'none',
                    color: task.is_completed ? 'text.tertiary' : 'text.primary',
                    fontWeight: 500,
                }}>{task.title}</Typography>
                {task.modules?.length > 0 && task.modules.map(m => (
                    <Typography key={m.id} component={RouterLink} to={`/modules/${m.id}?fromPlan=v2&roadmapId=${roadmapId}&rmId=${moduleId}`} level="body-xs" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#5BA3E6' : '#1568C0', fontWeight: 600, fontSize: '0.7rem', textDecoration: 'none', display: 'block', '&:hover': { textDecoration: 'underline' } }}>
                        {m.title}
                    </Typography>
                ))}
                {!task.modules?.length && task.resource_hint && (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.7rem' }} noWrap>{task.resource_hint}</Typography>
                )}
                {isAdmin && !task.modules?.length && task.task_type === 'read' && (
                    <Typography level="body-xs" sx={{ color: '#D97706', fontSize: '0.6rem', fontWeight: 600 }}>⚠ нет контента</Typography>
                )}
            </Box>

            {task.time_minutes > 0 && (
                <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.65rem', whiteSpace: 'nowrap' }}>{task.time_minutes} мин</Typography>
            )}
            {error && <Typography level="body-xs" color="danger">{error}</Typography>}
            {canEditTask && (
                <>
                    {!isVideoTask && task.modules?.length > 0 && (
                        <IconButton size="sm" variant="plain" color="danger" onClick={handleUnlink} loading={unlinking}
                            sx={{ minWidth: 22, minHeight: 22, p: 0.25, opacity: 0.4, '&:hover': { opacity: 1 } }}>
                            <X size={12} />
                        </IconButton>
                    )}
                    <IconButton size="sm" variant="plain" color="neutral" onClick={() => isVideoTask ? setEditModalOpen(true) : setLinkModalOpen(true)}
                        sx={{ minWidth: 22, minHeight: 22, p: 0.25, opacity: 0.4, '&:hover': { opacity: 1 } }}>
                        <PencilSimple size={12} />
                    </IconButton>
                    <IconButton size="sm" variant="plain" color="danger" onClick={handleDelete}
                        sx={{ minWidth: 22, minHeight: 22, p: 0.25, opacity: 1, color: '#E03E3E', '&:hover': { bgcolor: 'rgba(224,62,62,0.12)' } }}>
                        <Trash size={12} />
                    </IconButton>
                    {!isVideoTask && <LinkModulesModal open={linkModalOpen} onClose={() => setLinkModalOpen(false)} task={task} token={token} onSave={onRefresh} />}
                    {isVideoTask && <EditTaskModal open={editModalOpen} onClose={() => setEditModalOpen(false)} task={task} token={token} onSave={onRefresh} />}
                </>
            )}
        </Box>
    );
}


// ─── Task Item (list layout — original) ───
function TaskItem({ task, isEnrolled, dayStatus, token, onRefresh, navigate, user, roadmapId, moduleId, tourTargets }) {
    const { openUpgradeModal } = useContext(LayoutContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [unlinking, setUnlinking] = useState(false);
    const isAdmin = user?.role === 'admin';
    const isVideoTask = task.task_type === 'video';
    const canEditTask = isAdmin && (task.task_type === 'read' || task.task_type === 'video');
    const hasSubtasks = task.subtasks?.length > 0;
    const tourId = getTourIdForTask(task.id, tourTargets);

    const handleUnlink = async () => {
        setUnlinking(true);
        try { await APILinkTaskModules(token, task.id, []); onRefresh(); }
        catch (e) { console.error(e); }
        finally { setUnlinking(false); }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Удалить "${task.title}"?`)) return;
        try { await APIDeletePlanTask(token, task.id); onRefresh(); }
        catch (e) { console.error(e); }
    };

    const IconComponent = TASK_ICONS[task.task_type] || TASK_ICONS.other;
    const isGateType = task.task_type === 'gate' || task.task_type === 'final_gate';
    const isTestType = isGateType || task.task_type === 'mini_quiz' || task.task_type === 'qbank';
    const isLocked = dayStatus === 'locked';
    const canInteract = isEnrolled && dayStatus === 'active' && !task.is_completed;
    const canUncheck = isEnrolled && dayStatus === 'active' && task.is_completed && !isTestType;

    const handleToggleComplete = async () => {
        if (hasSubtasks) return; // Parent with subtasks auto-completes via children
        if (isGateType || isTestType) return;
        if (!task.is_completed && !canInteract) return;
        if (task.is_completed && !canUncheck) return;
        setLoading(true);
        setError(null);
        try {
            if (task.is_completed) await APIUncompleteTask(token, task.id);
            else await APICompleteTask(token, task.id);
            onRefresh();
        } catch (e) {
            const trialLock = detectTrialLocked(e);
            if (trialLock) { openUpgradeModal(trialLock.message); }
            else setError('Ошибка');
        }
        finally { setLoading(false); }
    };

    const handleStartTest = async () => {
        if (!isEnrolled || isLocked) return;
        if (task.test_id && !task.is_completed) { navigate(`/test-session/${task.test_id}`); return; }
        setLoading(true);
        setError(null);
        try {
            const res = await APIStartPlanTest(token, task.id);
            if (res.success && res.test_id) navigate(`/test-session/${res.test_id}`);
        } catch (e) {
            const trialLock = detectTrialLocked(e);
            if (trialLock) { openUpgradeModal(trialLock.message); }
            else setError(e.response?.data?.error || 'Не удалось создать тест');
        }
        finally { setLoading(false); }
    };

    const iconColor = task.is_completed ? STATE.completed : isLocked ? STATE.locked : ICON_NEUTRAL;

    return (
        <Box {...(tourId && { 'data-tour-id': tourId })}>
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, px: 1.5,
                borderRadius: '8px', opacity: isLocked ? 0.5 : 1,
                bgcolor: (theme) => task.is_completed
                    ? (theme.palette.mode === 'dark' ? '#282e25' : '#f2f4ef')
                    : isGateType && dayStatus === 'active' && !task.is_completed
                    ? (theme.palette.mode === 'dark' ? '#2d2523' : '#fdf5f2')
                    : (theme.palette.mode === 'dark' ? '#2c2c2c' : 'transparent'),
                transition: 'background-color 0.15s',
                '&:hover': !isLocked ? {
                    bgcolor: (theme) => task.is_completed
                        ? (theme.palette.mode === 'dark' ? '#2e3528' : '#edf0e8')
                        : (theme.palette.mode === 'dark' ? '#333' : '#EFECE4'),
                } : {},
            }}>
                {isGateType ? (
                    <Box sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
                        bgcolor: task.gate_passed ? 'rgba(15,123,108,0.12)' : task.test_score > 0 ? 'rgba(184,76,49,0.1)' : 'transparent' }}>
                        {task.gate_passed ? <CheckCircle size={20} color={STATE.completed} weight="fill" />
                            : <Lock size={18} color={task.test_score > 0 ? STATE.active : STATE.locked} />}
                    </Box>
                ) : !hasSubtasks ? (
                    <Checkbox checked={task.is_completed} disabled={isTestType || loading || isLocked || (!canInteract && !canUncheck)}
                        onChange={handleToggleComplete} size="sm" sx={{ '& .MuiCheckbox-checkbox': { borderRadius: '50%', borderColor: (theme) => theme.palette.mode === 'dark' ? '#888' : '#999', borderWidth: '2px' } }} />
                ) : null}

                {!hasSubtasks && <IconComponent size={18} weight="bold" color={iconColor} />}

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography className="task-title" level="body-sm" sx={{
                        textDecoration: task.is_completed && !isGateType ? 'line-through' : 'none',
                        color: task.is_completed ? 'text.tertiary' : isLocked ? 'text.tertiary' : 'text.primary',
                        fontWeight: task.is_completed ? 400 : isGateType ? 600 : 500,
                    }}>
                        {isGateType ? 'Контрольный тест' : task.title}
                        {(task.task_type === 'mini_quiz' || task.task_type === 'qbank') && task.questions_count > 0 && (
                            <span style={{ fontWeight: 400, color: 'inherit', opacity: 0.6 }}> ({task.questions_count})</span>
                        )}
                        {isGateType && task.questions_count > 0 && (
                            <span style={{ fontWeight: 400, color: 'inherit', opacity: 0.5 }}> ({task.questions_count} задач)</span>
                        )}
                    </Typography>
                    {!hasSubtasks && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                {task.modules?.length > 0 ? (
                                    task.modules.map(m => (
                                        <Typography key={m.id} component={RouterLink} to={`/modules/${m.id}?fromPlan=v2&roadmapId=${roadmapId}&rmId=${moduleId}`} level="body-xs" sx={{ mt: 0.25, color: (theme) => theme.palette.mode === 'dark' ? '#5BA3E6' : '#1568C0', fontWeight: 600, textDecoration: 'none', display: 'block', '&:hover': { textDecoration: 'underline' } }}>
                                            {m.title}
                                        </Typography>
                                    ))
                                ) : task.resource_hint ? (
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.25 }} noWrap>{task.resource_hint}</Typography>
                                ) : null}
                            </Box>
                            {canEditTask && (
                                <>
                                    {!isVideoTask && task.modules?.length > 0 && (
                                        <IconButton size="sm" variant="plain" color="danger" onClick={handleUnlink} loading={unlinking}
                                            sx={{ mt: 0.25, minWidth: 24, minHeight: 24, p: 0.25, opacity: 0.4, '&:hover': { opacity: 1 } }}>
                                            <X size={14} />
                                        </IconButton>
                                    )}
                                    <IconButton size="sm" variant="plain" color="neutral" onClick={() => isVideoTask ? setEditModalOpen(true) : setLinkModalOpen(true)}
                                        sx={{ mt: 0.25, minWidth: 24, minHeight: 24, p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}>
                                        <PencilSimple size={14} />
                                    </IconButton>
                                    <IconButton size="sm" variant="plain" color="danger" onClick={handleDelete}
                                        sx={{ mt: 0.25, minWidth: 24, minHeight: 24, p: 0.25, opacity: 1, color: '#E03E3E', '&:hover': { bgcolor: 'rgba(224,62,62,0.12)' } }}>
                                        <Trash size={14} />
                                    </IconButton>
                                </>
                            )}
                        </Box>
                    )}
                </Box>

                {hasSubtasks && (
                    <Chip size="sm" variant={task.is_completed ? 'soft' : 'outlined'} color={task.is_completed ? 'success' : 'neutral'}
                        sx={{ fontSize: '0.7rem', ...(task.is_completed && { bgcolor: 'rgba(120,140,93,0.15)', color: '#788C5D' }) }}>
                        {task.subtasks_completed}/{task.subtasks_total}
                    </Chip>
                )}

                {!hasSubtasks && task.time_minutes > 0 && (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{task.time_minutes} мин</Typography>
                )}

                {isGateType && task.test_score > 0 && !task.gate_passed && (
                    <Chip size="sm" variant="outlined" sx={{ ...getScoreStyle(task.test_score), border: '1px solid', fontWeight: 600, fontSize: '0.75rem' }}>{task.test_score}%</Chip>
                )}
                {isGateType && task.gate_passed && (
                    <Chip size="sm" variant="outlined" sx={{ ...getScoreStyle(task.test_score), border: '1px solid', fontWeight: 600, fontSize: '0.75rem' }}>{task.test_score}% ✓</Chip>
                )}

                {isTestType && !task.is_completed && dayStatus === 'active' && isEnrolled && (
                    isGateType ? (
                        <Button size="sm" variant="solid" onClick={handleStartTest} loading={loading}
                            sx={{ minWidth: 90, fontSize: '0.75rem', fontWeight: 600, bgcolor: '#B84C31', color: '#fff', '&:hover': { bgcolor: '#9A3F28' } }}>
                            {task.test_id ? 'Продолжить' : 'Решить'}
                        </Button>
                    ) : (
                        <Button size="sm" variant="outlined" onClick={handleStartTest} loading={loading}
                            sx={{ minWidth: 70, fontSize: '0.75rem', fontWeight: 600, color: '#DA7756', borderColor: '#DA7756', '&:hover': { bgcolor: 'rgba(218,119,86,0.08)', borderColor: '#B84C31' } }}>Решить</Button>
                    )
                )}
                {error && <Typography level="body-xs" color="danger">{error}</Typography>}
                {!hasSubtasks && canEditTask && !isVideoTask && <LinkModulesModal open={linkModalOpen} onClose={() => setLinkModalOpen(false)} task={task} token={token} onSave={onRefresh} />}
                {!hasSubtasks && canEditTask && isVideoTask && <EditTaskModal open={editModalOpen} onClose={() => setEditModalOpen(false)} task={task} token={token} onSave={onRefresh} />}
            </Box>

            {/* Subtasks */}
            {hasSubtasks && (
                <Box sx={{ pl: 5, pr: 1, mt: 0.25, mb: 0.5, borderLeft: '2px solid', borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', ml: 3 }}>
                    {task.subtasks.map((st, idx) => (
                        <SubTaskItem key={st.id} task={st} parentOrder={task.order} index={idx}
                            isEnrolled={isEnrolled} dayStatus={dayStatus} token={token} onRefresh={onRefresh} navigate={navigate} user={user} roadmapId={roadmapId} moduleId={moduleId} tourTargets={tourTargets} />
                    ))}
                </Box>
            )}
        </Box>
    );
}


// ─── Sub-Task Row (table layout — for children) ───
function SubTaskRow({ task, parentOrder, index, isEnrolled, dayStatus, token, onRefresh, user, navigate, roadmapId, moduleId, tourTargets }) {
    const { openUpgradeModal } = useContext(LayoutContext);
    const [loading, setLoading] = useState(false);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [unlinking, setUnlinking] = useState(false);
    const isAdmin = user?.role === 'admin';
    const isVideoTask = task.task_type === 'video';
    const canEditTask = isAdmin && (task.task_type === 'read' || task.task_type === 'video');
    const tourId = getTourIdForTask(task.id, tourTargets);

    const handleUnlink = async () => {
        setUnlinking(true);
        try { await APILinkTaskModules(token, task.id, []); onRefresh(); }
        catch (e) { console.error(e); }
        finally { setUnlinking(false); }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Удалить "${task.title}"?`)) return;
        try { await APIDeletePlanTask(token, task.id); onRefresh(); }
        catch (e) { console.error(e); }
    };

    const isLocked = dayStatus === 'locked';
    const canInteract = isEnrolled && dayStatus === 'active' && !task.is_completed;
    const canUncheck = isEnrolled && dayStatus === 'active' && task.is_completed;

    const handleToggle = async () => {
        if (!task.is_completed && !canInteract) return;
        if (task.is_completed && !canUncheck) return;
        setLoading(true);
        try {
            if (task.is_completed) await APIUncompleteTask(token, task.id);
            else await APICompleteTask(token, task.id);
            onRefresh();
        } catch (e) {
            const trialLock = detectTrialLocked(e);
            if (trialLock) { openUpgradeModal(trialLock.message); }
        }
        finally { setLoading(false); }
    };

    const label = `${parentOrder}.${index + 1}`;

    return (
        <Box
            component="tr"
            {...(tourId && { 'data-tour-id': tourId })}
            sx={{
            opacity: isLocked ? 0.5 : 1,
            bgcolor: (theme) => task.is_completed
                ? (theme.palette.mode === 'dark' ? '#282e25' : '#f2f4ef')
                : (theme.palette.mode === 'dark' ? '#2c2c2c' : 'transparent'),
            '& td': { py: 0.5, px: 1, borderBottom: '1px solid', borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', verticalAlign: 'middle' },
        }}>
            <td>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Checkbox checked={task.is_completed} disabled={loading || isLocked || (!canInteract && !canUncheck)}
                        onChange={handleToggle} size="sm" sx={{ '& .MuiCheckbox-checkbox': { borderRadius: '50%', borderColor: (theme) => theme.palette.mode === 'dark' ? '#888' : '#999', borderWidth: '2px' } }} />
                </Box>
            </td>
            <td>
                {/* empty step column for subtasks */}
            </td>
            <td>
                <Typography level="body-xs" sx={{
                    pl: 2,
                    textDecoration: task.is_completed ? 'line-through' : 'none',
                    color: task.is_completed ? 'text.tertiary' : 'text.primary',
                    fontWeight: 400,
                }}>{task.title}</Typography>
            </td>
            <td>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {task.modules?.length > 0 ? task.modules.map(m => (
                            <Typography key={m.id} component={RouterLink} to={`/modules/${m.id}?fromPlan=v2&roadmapId=${roadmapId}&rmId=${moduleId}`} level="body-xs" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#5BA3E6' : '#1568C0', fontWeight: 600, fontSize: '0.7rem', textDecoration: 'none', display: 'block', '&:hover': { textDecoration: 'underline' } }}>
                                {m.title}
                            </Typography>
                        )) : task.resource_hint ? (
                            <Typography level="body-xs" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>{task.resource_hint}</Typography>
                        ) : null}
                        {isAdmin && !task.modules?.length && task.task_type === 'read' && (
                            <Typography level="body-xs" sx={{ color: '#D97706', fontSize: '0.6rem', fontWeight: 600 }}>⚠ нет контента</Typography>
                        )}
                    </Box>
                    {canEditTask && (
                        <>
                            {!isVideoTask && task.modules?.length > 0 && (
                                <IconButton size="sm" variant="plain" color="danger" onClick={handleUnlink} loading={unlinking}
                                    sx={{ minWidth: 20, minHeight: 20, p: 0.2, opacity: 0.4, '&:hover': { opacity: 1 } }}>
                                    <X size={12} />
                                </IconButton>
                            )}
                            <IconButton size="sm" variant="plain" color="neutral" onClick={() => isVideoTask ? setEditModalOpen(true) : setLinkModalOpen(true)}
                                sx={{ minWidth: 20, minHeight: 20, p: 0.2, opacity: 0.4, '&:hover': { opacity: 1 } }}>
                                <PencilSimple size={12} />
                            </IconButton>
                            <IconButton size="sm" variant="plain" color="danger" onClick={handleDelete}
                                sx={{ minWidth: 20, minHeight: 20, p: 0.2, opacity: 1, color: '#E03E3E', '&:hover': { bgcolor: 'rgba(224,62,62,0.12)' } }}>
                                <Trash size={12} />
                            </IconButton>
                        </>
                    )}
                </Box>
                {canEditTask && !isVideoTask && <LinkModulesModal open={linkModalOpen} onClose={() => setLinkModalOpen(false)} task={task} token={token} onSave={onRefresh} />}
                {canEditTask && isVideoTask && <EditTaskModal open={editModalOpen} onClose={() => setEditModalOpen(false)} task={task} token={token} onSave={onRefresh} />}
            </td>
            <td>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', whiteSpace: 'nowrap', textAlign: 'right', fontSize: '0.65rem' }}>
                    {task.time_minutes > 0 ? `${task.time_minutes} мин` : '—'}
                </Typography>
            </td>
        </Box>
    );
}


// ─── Task Row (table layout) ───
function TaskRow({ task, isEnrolled, dayStatus, token, onRefresh, navigate, user, roadmapId, moduleId, tourTargets }) {
    const { openUpgradeModal } = useContext(LayoutContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [unlinking, setUnlinking] = useState(false);
    const isAdmin = user?.role === 'admin';
    const isVideoTask = task.task_type === 'video';
    const canEditTask = isAdmin && (task.task_type === 'read' || task.task_type === 'video');
    const hasSubtasks = task.subtasks?.length > 0;
    const tourId = getTourIdForTask(task.id, tourTargets);

    const handleUnlink = async () => {
        setUnlinking(true);
        try { await APILinkTaskModules(token, task.id, []); onRefresh(); }
        catch (e) { console.error(e); }
        finally { setUnlinking(false); }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Удалить "${task.title}"?`)) return;
        try { await APIDeletePlanTask(token, task.id); onRefresh(); }
        catch (e) { console.error(e); }
    };

    const isGateType = task.task_type === 'gate' || task.task_type === 'final_gate';
    const isTestType = isGateType || task.task_type === 'mini_quiz' || task.task_type === 'qbank';
    const isLocked = dayStatus === 'locked';
    const canInteract = isEnrolled && dayStatus === 'active' && !task.is_completed;
    const canUncheck = isEnrolled && dayStatus === 'active' && task.is_completed && !isTestType;

    const handleToggleComplete = async () => {
        if (hasSubtasks) return; // Parent with subtasks auto-completes via children
        if (isGateType || isTestType) return;
        if (!task.is_completed && !canInteract) return;
        if (task.is_completed && !canUncheck) return;
        setLoading(true);
        setError(null);
        try {
            if (task.is_completed) await APIUncompleteTask(token, task.id);
            else await APICompleteTask(token, task.id);
            onRefresh();
        } catch (e) {
            const trialLock = detectTrialLocked(e);
            if (trialLock) { openUpgradeModal(trialLock.message); }
            else setError('Ошибка');
        }
        finally { setLoading(false); }
    };

    const handleStartTest = async () => {
        if (!isEnrolled || isLocked) return;
        if (task.test_id && !task.is_completed) { navigate(`/test-session/${task.test_id}`); return; }
        setLoading(true);
        setError(null);
        try {
            const res = await APIStartPlanTest(token, task.id);
            if (res.success && res.test_id) navigate(`/test-session/${res.test_id}`);
        } catch (e) {
            const trialLock = detectTrialLocked(e);
            if (trialLock) { openUpgradeModal(trialLock.message); }
            else setError(e.response?.data?.error || 'Не удалось создать тест');
        }
        finally { setLoading(false); }
    };

    // rowBg is not used directly — theme-adaptive colors applied inline

    // Build resource text for non-module tasks
    const hasModules = task.modules?.length > 0;
    const resourceText = hasModules ? '' : (
        task.resource_hint
        || (isGateType ? `QBank (${task.questions_count} вопросов, порог ${task.pass_threshold}%)` : '')
        || (task.task_type === 'qbank' || task.task_type === 'mini_quiz' ? `мини-тест (${task.questions_count} вопросов)` : '')
        || (task.task_type === 'video' && task.video_url ? task.video_url : '')
        || (task.task_type === 'anki_new' || task.task_type === 'anki_review' ? `Anki → ${task.anki_deck_name || 'колода'}` : '')
        || ''
    );

    return (
        <>
            <Box
                component="tr"
                {...(tourId && { 'data-tour-id': tourId })}
                sx={{ opacity: isLocked ? 0.5 : 1, '& td': { py: 0.75, px: 1, borderBottom: '1px solid', borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', verticalAlign: 'middle' } }}>
                {/* Статус */}
                <td>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {isGateType ? (
                            task.gate_passed
                                ? <CheckCircle size={18} color={STATE.completed} weight="fill" />
                                : <Lock size={16} color={task.test_score > 0 ? STATE.active : STATE.locked} />
                        ) : !hasSubtasks ? (
                            <Checkbox checked={task.is_completed} disabled={isTestType || loading || isLocked || (!canInteract && !canUncheck)}
                                onChange={handleToggleComplete} size="sm" sx={{ '& .MuiCheckbox-checkbox': { borderRadius: '50%', borderColor: (theme) => theme.palette.mode === 'dark' ? '#888' : '#999', borderWidth: '2px' } }} />
                        ) : null}
                    </Box>
                </td>
                {/* Шаг */}
                <td>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'center' }}>{task.order}</Typography>
                </td>
                {/* Действие */}
                <td>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography level="body-xs" sx={{
                            textDecoration: task.is_completed && !isGateType ? 'line-through' : 'none',
                            color: task.is_completed ? 'text.tertiary' : isLocked ? 'text.tertiary' : 'text.primary',
                            fontWeight: task.is_completed ? 400 : isGateType ? 600 : 500,
                        }}>
                            {isGateType ? 'Контрольный тест' : task.title}
                            {(task.task_type === 'mini_quiz' || task.task_type === 'qbank') && task.questions_count > 0 && (
                                <span style={{ fontWeight: 400, color: 'inherit', opacity: 0.6 }}> ({task.questions_count})</span>
                            )}
                            {isGateType && task.questions_count > 0 && (
                                <span style={{ fontWeight: 400, color: 'inherit', opacity: 0.5 }}> ({task.questions_count} задач)</span>
                            )}
                        </Typography>
                        {hasSubtasks && (
                            <Chip size="sm" variant={task.is_completed ? 'soft' : 'outlined'}
                                color={task.is_completed ? 'success' : 'neutral'} sx={{ fontSize: '0.6rem', ...(task.is_completed && { bgcolor: 'rgba(120,140,93,0.15)', color: '#788C5D' }) }}>
                                {task.subtasks_completed}/{task.subtasks_total}
                            </Chip>
                        )}
                    </Box>
                </td>
                {/* Ресурс */}
                <td>
                    {!hasSubtasks && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {hasModules ? (
                                        task.modules.map(m => (
                                            <Typography key={m.id} component={RouterLink} to={`/modules/${m.id}?fromPlan=v2&roadmapId=${roadmapId}&rmId=${moduleId}`} level="body-xs" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#5BA3E6' : '#1568C0', fontWeight: 600, textDecoration: 'none', display: 'block', '&:hover': { textDecoration: 'underline' } }}>
                                                {m.title}
                                            </Typography>
                                        ))
                                    ) : resourceText ? (
                                        task.task_type === 'video' && task.video_url ? (
                                            <Typography level="body-xs" component="a" href={task.video_url} target="_blank" rel="noopener noreferrer"
                                                sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#5BA3E6' : '#1568C0', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                                {task.resource_hint || task.title}
                                            </Typography>
                                        ) : (
                                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{resourceText}</Typography>
                                        )
                                    ) : null}
                                </Box>
                                {canEditTask && (
                                    <>
                                        {!isVideoTask && task.modules?.length > 0 && (
                                            <IconButton size="sm" variant="plain" color="danger" onClick={handleUnlink} loading={unlinking}
                                                sx={{ minWidth: 22, minHeight: 22, p: 0.25, opacity: 0.4, '&:hover': { opacity: 1 } }}>
                                                <X size={13} />
                                            </IconButton>
                                        )}
                                        <IconButton size="sm" variant="plain" color="neutral" onClick={() => isVideoTask ? setEditModalOpen(true) : setLinkModalOpen(true)}
                                            sx={{ minWidth: 22, minHeight: 22, p: 0.25, opacity: 0.4, '&:hover': { opacity: 1 } }}>
                                            <PencilSimple size={13} />
                                        </IconButton>
                                        <IconButton size="sm" variant="plain" color="danger" onClick={handleDelete}
                                            sx={{ minWidth: 22, minHeight: 22, p: 0.25, opacity: 1, color: '#E03E3E', '&:hover': { bgcolor: 'rgba(224,62,62,0.12)' } }}>
                                            <Trash size={13} />
                                        </IconButton>
                                    </>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                {isGateType && task.gate_passed && (
                                    <Chip size="sm" variant="outlined" sx={{ ...getScoreStyle(task.test_score), border: '1px solid', fontWeight: 600, fontSize: '0.6rem' }}>{task.test_score}% ✓</Chip>
                                )}
                                {isGateType && task.test_score > 0 && !task.gate_passed && (
                                    <Chip size="sm" variant="outlined" sx={{ ...getScoreStyle(task.test_score), border: '1px solid', fontWeight: 600, fontSize: '0.6rem' }}>{task.test_score}%</Chip>
                                )}
                                {isTestType && !task.is_completed && dayStatus === 'active' && isEnrolled && (
                                    isGateType ? (
                                        <Button size="sm" variant="solid" onClick={handleStartTest} loading={loading}
                                            sx={{ fontSize: '0.65rem', fontWeight: 600, py: 0.2, px: 0.75, bgcolor: '#B84C31', color: '#fff', '&:hover': { bgcolor: '#9A3F28' } }}>
                                            {task.test_id ? 'Продолжить' : 'Решить'}
                                        </Button>
                                    ) : (
                                        <Button size="sm" variant="outlined" onClick={handleStartTest} loading={loading}
                                            sx={{ fontSize: '0.65rem', fontWeight: 600, py: 0.2, px: 0.75, color: '#DA7756', borderColor: '#DA7756', '&:hover': { bgcolor: 'rgba(218,119,86,0.08)', borderColor: '#B84C31' } }}>Решить</Button>
                                    )
                                )}
                                {error && <Typography level="body-xs" color="danger">{error}</Typography>}
                            </Box>
                        </Box>
                    )}
                    {!hasSubtasks && canEditTask && !isVideoTask && <LinkModulesModal open={linkModalOpen} onClose={() => setLinkModalOpen(false)} task={task} token={token} onSave={onRefresh} />}
                    {!hasSubtasks && canEditTask && isVideoTask && <EditTaskModal open={editModalOpen} onClose={() => setEditModalOpen(false)} task={task} token={token} onSave={onRefresh} />}
                </td>
                {/* Время */}
                <td>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {task.time_minutes > 0 ? `${task.time_minutes} мин` : '—'}
                    </Typography>
                </td>
            </Box>
            {/* Subtask rows */}
            {hasSubtasks && task.subtasks.map((st, idx) => (
                <SubTaskRow key={st.id} task={st} parentOrder={task.order} index={idx}
                    isEnrolled={isEnrolled} dayStatus={dayStatus} token={token} onRefresh={onRefresh} user={user} navigate={navigate} roadmapId={roadmapId} moduleId={moduleId} tourTargets={tourTargets} />
            ))}
        </>
    );
}


// ─── Day Section ───
export function DaySection({ day, isEnrolled, isCurrent, token, onRefresh, navigate, taskLayout, user, roadmapId, moduleId, enableTourTargets, freeMode }) {
    const { openUpgradeModal } = useContext(LayoutContext);
    // В свободном режиме активны сразу все дни, и автораскрытие превратило бы
    // страницу в стену из всего материала модуля (в Неврологии это 22 блока).
    // Поэтому раскрываем сами только в обычном режиме.
    const [open, setOpen] = useState(day.status === 'active' && !freeMode);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const isLocked = day.status === 'locked';
    const isCompleted = day.status === 'completed';
    const isAdmin = user?.role === 'admin';
    const isTrialLocked = day.trial_locked === true;

    // Only the active (unlocked) day of a trial user gets tour targets —
    // otherwise the tour could try to spotlight a locked day's tasks.
    const tourTargets = useMemo(() => {
        if (!enableTourTargets || day.status !== 'active') return null;
        return computeTourTargets(day.tasks);
    }, [enableTourTargets, day.status, day.tasks]);

    // Trial-locked день: вместо "просто locked" кликается, открывает UpgradeModal.
    const handleHeaderClick = () => {
        if (isTrialLocked) {
            openUpgradeModal(`День ${day.day_number} откроется после оформления полного доступа.`);
            return;
        }
        if (!isLocked) setOpen(!open);
    };

    return (
        <Box sx={{
            position: 'relative',
            bgcolor: (theme) => theme.palette.mode === 'dark' ? '#262626' : '#FAF9F5',
            border: '1px solid',
            borderColor: (theme) => isTrialLocked
                ? 'rgba(184, 76, 49, 0.4)'
                : isCurrent
                    ? (theme.palette.mode === 'dark' ? '#DA7756' : '#B84C31')
                    : (theme.palette.mode === 'dark' ? '#3a3a3a' : '#e5e5e5'),
            borderRadius: '12px',
            overflow: 'hidden',
            borderLeft: isCurrent ? '3px solid' : '1px solid',
            borderLeftColor: (theme) => isCurrent
                ? (theme.palette.mode === 'dark' ? '#DA7756' : '#B84C31')
                : (theme.palette.mode === 'dark' ? '#3a3a3a' : '#e5e5e5'),
        }}>
            <Box
                onClick={handleHeaderClick}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: 2,
                    cursor: (isTrialLocked || !isLocked) ? 'pointer' : 'default',
                    borderRadius: '10px',
                    opacity: isTrialLocked ? 0.72 : (isLocked ? 0.45 : 1),
                    bgcolor: isTrialLocked ? 'rgba(184, 76, 49, 0.04)' : 'transparent',
                    transition: 'all 0.15s',
                    '&:hover': (isTrialLocked || !isLocked) ? {
                        bgcolor: (theme) => isTrialLocked
                            ? 'rgba(184, 76, 49, 0.08)'
                            : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'),
                    } : {},
                }}
            >
                {isTrialLocked ? <Lock size={20} color="#B84C31" weight="fill" />
                    : isCompleted ? <CheckCircle size={22} color={STATE.completed} weight="fill" />
                    : isLocked ? <Lock size={20} color={STATE.locked} />
                    : <Circle size={22} color={STATE.active} />}

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="title-sm" sx={{
                        fontWeight: 600,
                        color: (isLocked || isTrialLocked) ? 'text.tertiary' : 'text.primary',
                    }}>
                        {isCurrent ? 'Твой план на сегодня' : `День ${day.day_number}`}{day.is_final_gate && '. Финальный тест'}
                    </Typography>
                    <Typography level="body-xs" sx={{
                        color: (isLocked || isTrialLocked) ? 'text.tertiary' : 'text.secondary',
                    }} noWrap>
                        {isCurrent ? `День ${day.day_number} · ${day.title}` : day.title}
                    </Typography>
                </Box>

                {isTrialLocked ? (
                    <Chip size="sm" variant="soft" color="danger" sx={{ fontWeight: 600 }}>
                        Открыть
                    </Chip>
                ) : !isLocked && (
                    <Chip size="sm" variant={isCompleted ? 'soft' : 'outlined'} color={isCompleted ? 'success' : 'neutral'}
                        sx={isCompleted ? { bgcolor: 'rgba(120,140,93,0.15)', color: '#788C5D' } : undefined}>
                        {day.tasks_completed}/{day.tasks_total}
                    </Chip>
                )}
                {isAdmin && (
                    <IconButton size="sm" variant="plain" color="neutral"
                        onClick={(e) => { e.stopPropagation(); setAddModalOpen(true); }}
                        sx={{ minWidth: 22, minHeight: 22, p: 0.25, opacity: 0.4, '&:hover': { opacity: 1 } }}>
                        <Plus size={13} />
                    </IconButton>
                )}
                {!isLocked && !isTrialLocked && (
                    <Box sx={{ color: 'text.tertiary', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'flex' }}>
                        <CaretDown size={18} />
                    </Box>
                )}
            </Box>
            {isAdmin && <EditTaskModal open={addModalOpen} onClose={() => setAddModalOpen(false)} task={null} dayId={day.id} token={token} onSave={() => { onRefresh(); setOpen(true); }} />}

            {open && !isLocked && !isTrialLocked && (
                taskLayout === 'table' ? (
                    <Box sx={{
                        px: 1, pb: 1, overflowX: 'hidden',
                        '--row-done': (theme) => theme.palette.mode === 'dark' ? 'rgba(120,140,93,0.08)' : 'rgba(120,140,93,0.05)',
                        '--row-gate': (theme) => theme.palette.mode === 'dark' ? 'rgba(184,76,49,0.06)' : 'rgba(184,76,49,0.03)',
                    }}>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse',
                            '& th': { py: 0.5, textAlign: 'left', borderBottom: '2px solid', borderColor: 'divider', fontSize: '0.65rem', color: 'text.tertiary', fontWeight: 600 },
                        }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 36, textAlign: 'center' }}>Статус</th>
                                    <th style={{ width: 32, textAlign: 'center' }}>Шаг</th>
                                    <th style={{ paddingLeft: 4 }}>Действие</th>
                                    <th style={{ paddingLeft: 4 }}>Ресурс</th>
                                    <th style={{ width: 80, textAlign: 'right', paddingRight: 8 }}>Время</th>
                                </tr>
                            </thead>
                            <tbody>
                                {day.tasks.map((task) => (
                                    <TaskRow key={task.id} task={task} isEnrolled={isEnrolled} dayStatus={day.status} token={token} onRefresh={onRefresh} navigate={navigate} user={user} roadmapId={roadmapId} moduleId={moduleId} tourTargets={tourTargets} />
                                ))}
                            </tbody>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ pl: 4.5, pr: 1, pb: 1 }}>
                        {day.tasks.map((task) => (
                            <TaskItem key={task.id} task={task} isEnrolled={isEnrolled} dayStatus={day.status} token={token} onRefresh={onRefresh} navigate={navigate} user={user} roadmapId={roadmapId} moduleId={moduleId} tourTargets={tourTargets} />
                        ))}
                    </Box>
                )
            )}
        </Box>
    );
}


// ─── Module Card ───
function ModuleCard({ mod, isCurrent, onSelect, index, user, token, onToggle, dataTourId }) {
    const { openUpgradeModal } = useContext(LayoutContext);
    const [toggling, setToggling] = useState(false);
    const isLocked = mod.status === 'locked';
    const isCompleted = mod.status === 'completed';
    const isAdmin = user?.role === 'admin';
    const isTrialLocked = mod.trial_locked === true;

    const handleToggle = async (e) => {
        e.stopPropagation();
        if (toggling) return;
        setToggling(true);
        try {
            await APITogglePlanModule(token, mod.id);
            onToggle?.();
        } catch (err) { console.error(err); }
        finally { setToggling(false); }
    };

    const handleClick = () => {
        if (isTrialLocked) {
            openUpgradeModal(`«${mod.title}» откроется после оформления полного доступа.`);
            return;
        }
        if (!isLocked) onSelect(mod.id);
    };

    return (
        <Box
            onClick={handleClick}
            {...(dataTourId && { 'data-tour-id': dataTourId })}
            sx={{
                p: 2, borderRadius: '12px',
                cursor: isTrialLocked ? 'pointer' : (isLocked ? 'default' : 'pointer'),
                opacity: isTrialLocked ? 0.72 : (isLocked ? 0.5 : 1),
                bgcolor: (theme) => isTrialLocked
                    ? 'rgba(184, 76, 49, 0.04)'
                    : isCompleted
                        ? (theme.palette.mode === 'dark' ? '#252820' : '#f4f5f0')
                        : (theme.palette.mode === 'dark' ? '#2f2f2f' : '#FAF9F5'),
                border: '1px solid',
                borderColor: (theme) => isTrialLocked
                    ? 'rgba(184, 76, 49, 0.4)'
                    : isCompleted
                        ? (theme.palette.mode === 'dark' ? '#4a5038' : '#c5ccb0')
                        : (theme.palette.mode === 'dark' ? '#3a3a3a' : '#e0e0e0'),
                transition: 'all 0.15s',
                '&:hover': (isTrialLocked || !isLocked) ? {
                    transform: 'translateY(-1px)',
                    boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)',
                    borderColor: (theme) => isTrialLocked
                        ? '#B84C31'
                        : isCompleted
                            ? (theme.palette.mode === 'dark' ? '#5a6a48' : '#a8b892')
                            : (theme.palette.mode === 'dark' ? '#4a4a4a' : '#c0c0c0'),
                } : {},
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                {/* Lock icon for trial-locked, otherwise circle indicator */}
                {isTrialLocked ? (
                    <Box sx={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Lock size={16} weight="fill" color="#B84C31" />
                    </Box>
                ) : (
                    <Box
                        onClick={isAdmin ? handleToggle : undefined}
                        sx={{
                            width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                            border: isCompleted ? '2px solid #788C5D' : '2px solid #666',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: isAdmin ? 'pointer' : 'default',
                            transition: 'border-color 0.15s, transform 0.1s',
                            '&:hover': isAdmin ? { borderColor: isCompleted ? '#96a87e' : '#999', transform: 'scale(1.15)' } : {},
                        }}
                    >
                        {isCompleted && <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#788C5D' }} />}
                        {toggling && <CircularProgress size="sm" sx={{ '--CircularProgress-size': '14px' }} />}
                    </Box>
                )}
                <Typography level="title-sm" sx={{
                    flex: 1, fontWeight: 600,
                    color: isTrialLocked
                        ? 'text.secondary'
                        : isCompleted ? '#788C5D' : isLocked ? 'text.tertiary' : 'text.primary',
                    textDecoration: isCompleted ? 'line-through' : 'none',
                    textDecorationColor: isCompleted ? '#a8b892' : undefined,
                }}>
                    {index}. {mod.title}
                </Typography>
                {isAdmin && mod.is_restructured && (
                    <Box sx={{ px: 0.75, py: 0.25, borderRadius: '4px', bgcolor: 'rgba(42,149,120,0.15)', border: '1px solid rgba(42,149,120,0.35)', flexShrink: 0 }}>
                        <Typography level="body-xs" sx={{ color: '#2a9578', fontWeight: 600, fontSize: '0.6rem', letterSpacing: '0.03em' }}>SLAM✓</Typography>
                    </Box>
                )}
                {isTrialLocked ? (
                    <Chip size="sm" variant="soft" color="danger" sx={{ fontWeight: 600, flexShrink: 0 }}>
                        Открыть
                    </Chip>
                ) : (
                    <Typography level="body-sm" sx={{ color: isCompleted ? '#788C5D' : '#D97757', fontWeight: 500, flexShrink: 0 }}>
                        {isCompleted ? 'Пройден' : `${mod.completed_days}/${mod.total_days} дней`}
                    </Typography>
                )}
            </Box>
            {!isLocked && !isTrialLocked && (
                <LinearProgress determinate value={mod.progress_percent}
                    sx={{ '--LinearProgress-thickness': '8px', color: isCompleted ? '#788C5D' : '#D97757', borderRadius: '4px', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)', mt: 0.5 }} />
            )}
        </Box>
    );
}


// ─── Module Detail View ───
function ModuleDetailView({ moduleId, roadmapId, token, onBack, navigate, user, enableTourTargets, onRestartTour }) {
    const { trialInfo, openUpgradeModal } = useContext(LayoutContext);
    const [module, setModule] = useState(null);
    const [isEnrolled, setIsEnrolled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [taskLayout, setTaskLayout] = useState(() => localStorage.getItem('plans_task_layout') || 'table');

    const toggleTaskLayout = (mode) => {
        setTaskLayout(mode);
        localStorage.setItem('plans_task_layout', mode);
    };

    const fetchModule = useCallback(async () => {
        try {
            const res = await APIGetPlanModuleDetail(token, moduleId);
            if (res.success) { setModule(res.module); setIsEnrolled(res.is_enrolled); }
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [token, moduleId]);

    const [freeModeBusy, setFreeModeBusy] = useState(false);

    const handleToggleFreeMode = async () => {
        setFreeModeBusy(true);
        try {
            const res = await APIToggleFreeMode(token, moduleId);
            if (res.success) await fetchModule();
        } catch (e) { console.error(e); }
        finally { setFreeModeBusy(false); }
    };

    useEffect(() => { fetchModule(); }, [fetchModule]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    if (!module) return <Alert color="danger">Модуль не найден</Alert>;

    const currentDayId = module.days.find(d => d.status === 'active')?.id;

    const isActiveTrial = trialInfo?.state === 'active_trial';
    const isTgPromo = trialInfo?.is_tg_promo === true;
    const trialDaysLeft = trialInfo?.days_left ?? 0;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <IconButton variant="plain" onClick={onBack} size="sm"><ArrowLeft size={18} /></IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="h4" sx={{ fontWeight: 700 }}>{module.title}</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        {module.duration_days} дней · {module.completed_days} завершено · {module.progress_percent}%
                    </Typography>
                </Box>
                {/* Tour restart + layout toggle */}
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    {onRestartTour && !enableTourTargets && (
                        <IconButton size="sm" variant="soft"
                            onClick={onRestartTour} title="Подсказки"
                            sx={{
                                bgcolor: 'rgba(218,119,86,0.12)',
                                color: '#DA7756',
                                '&:hover': { bgcolor: 'rgba(218,119,86,0.22)' },
                            }}>
                            <Question size={18} weight="bold" />
                        </IconButton>
                    )}
                    <IconButton size="sm" variant={taskLayout === 'list' ? 'soft' : 'plain'} color={taskLayout === 'list' ? 'primary' : 'neutral'}
                        onClick={() => toggleTaskLayout('list')} title="Список">
                        <List size={18} />
                    </IconButton>
                    <IconButton size="sm" variant={taskLayout === 'table' ? 'soft' : 'plain'} color={taskLayout === 'table' ? 'primary' : 'neutral'}
                        onClick={() => toggleTaskLayout('table')} title="Таблица">
                        <Table size={18} />
                    </IconButton>
                </Box>
                <Chip size="md" variant="soft"
                    color={module.status === 'completed' ? 'success' : module.status === 'active' ? 'warning' : 'neutral'}
                    sx={module.status === 'completed' ? { bgcolor: 'rgba(120,140,93,0.15)', color: '#788C5D' } : module.status === 'active' ? {
                        bgcolor: 'transparent',
                        border: '1.5px solid',
                        borderColor: (theme) => theme.palette.mode === 'dark' ? '#DA7756' : '#B84C31',
                        color: (theme) => theme.palette.mode === 'dark' ? '#DA7756' : '#B84C31',
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                    } : {}}>
                    {module.status === 'completed' ? 'Пройден' : module.status === 'active' ? 'В процессе' : 'Заблокирован'}
                </Chip>
            </Box>
            <LinearProgress determinate value={module.progress_percent}
                sx={{ mb: 3, '--LinearProgress-thickness': '6px', color: module.status === 'completed' ? '#788C5D' : '#B84C31', borderRadius: '3px' }} />

            {/* Свободный режим — платная функция. Бесплатному юзеру тумблер не
                показываем вообще: сервер такому всё равно отдаст 403. */}
            {module.free_mode_available && (
                <Sheet
                    variant="soft"
                    sx={(theme) => ({
                        mb: 3, p: 2, borderRadius: '12px',
                        display: 'flex', alignItems: 'center', gap: 2,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                        border: '0.5px solid',
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                    })}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                            Свободный режим
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            {module.free_mode
                                ? 'Все дни открыты, гейты не блокируют переход'
                                : 'Дни открываются по порядку'}
                        </Typography>
                    </Box>
                    <Button
                        size="sm"
                        variant={module.free_mode ? 'solid' : 'outlined'}
                        loading={freeModeBusy}
                        onClick={handleToggleFreeMode}
                        sx={{ flexShrink: 0 }}
                    >
                        {module.free_mode ? 'Вернуть стоп-уроки' : 'Убрать стоп-уроки'}
                    </Button>
                </Sheet>
            )}

            {/* Trial / TG promo upgrade CTA — видимая кнопка оплаты прямо в плане.
                Для tg_promo: своя копи + цены 1590/3990/6990 (показываются в UpgradeModal). */}
            {(isActiveTrial || isTgPromo) && (
                <Sheet
                    variant="soft"
                    sx={(theme) => ({
                        mb: 3,
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: '16px',
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(218,119,86,0.08)' : '#FFF8F0',
                        border: '1.5px solid #DA7756',
                    })}
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={2}
                    >
                        <Box sx={{
                            width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                            bgcolor: 'rgba(218, 119, 86, 0.15)',
                            display: { xs: 'none', sm: 'flex' },
                            alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Trophy size={24} color="#DA7756" weight="duotone" />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography level="title-sm" sx={{ fontWeight: 700, color: '#DA7756', mb: 0.25 }}>
                                {isTgPromo ? 'Открой весь курс по цене класса' : 'Открой полный курс'}
                            </Typography>
                            <Typography level="body-xs" color="neutral" sx={{ lineHeight: 1.5 }}>
                                {isTgPromo
                                    ? 'У тебя открыты БХ, Иммунология и Кардиология. Доплати по цене закрытого Telegram-класса от 1 590 ₽, чтобы открыть остальные блоки USMLE.'
                                    : `Триал: осталось ${trialDaysLeft} ${trialDaysLeft === 1 ? 'день' : trialDaysLeft >= 2 && trialDaysLeft <= 4 ? 'дня' : 'дней'}. Оформи APEX USMLE PRO и получи все планы, клинические задачи и видео без лимитов.`}
                            </Typography>
                        </Box>
                        <Button
                            variant="solid"
                            size="md"
                            onClick={() => openUpgradeModal(
                                isTgPromo
                                    ? 'Открой весь курс APEX по цене закрытого Telegram-класса.'
                                    : 'Открой полный доступ ко всем учебным планам.'
                            )}
                            sx={{
                                bgcolor: '#DA7756',
                                color: '#FFFFFF',
                                fontWeight: 700,
                                borderRadius: '10px',
                                px: 2.5,
                                flexShrink: 0,
                                width: { xs: '100%', sm: 'auto' },
                                '&:hover': { bgcolor: '#C4623E' },
                            }}
                        >
                            {isTgPromo ? 'Улучшить тариф' : 'Посмотреть тарифы'}
                        </Button>
                    </Stack>
                </Sheet>
            )}

            {/* Intro panel — Apple-style */}
            <Box sx={{
                mb: 3, p: { xs: 2.5, sm: 3 }, borderRadius: '16px',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#F9F8F6',
            }}>
                {module.intro_text ? (
                    <Box
                        sx={{
                            fontSize: '0.9rem', fontWeight: 500, color: 'text.primary',
                            lineHeight: 1.65, letterSpacing: '-0.01em', mb: 2,
                            '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
                            '& a': {
                                color: (theme) => theme.palette.mode === 'dark' ? '#DA7756' : '#B84C31',
                                fontWeight: 600, textDecoration: 'underline',
                                '&:hover': { textDecoration: 'none' },
                            },
                        }}
                        dangerouslySetInnerHTML={{ __html: module.intro_text }}
                    />
                ) : (
                    <Typography sx={{
                        fontSize: '0.9rem', fontWeight: 500, color: 'text.primary',
                        lineHeight: 1.65, letterSpacing: '-0.01em', mb: 2,
                    }}>
                        Добро пожаловать в этот блок плана. Впереди насыщенная работа с материалом, клинические задачи и проверочные тесты.
                    </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2.5 }}>
                    <Box sx={{
                        width: 48, height: 48, borderRadius: '12px', flexShrink: 0,
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <BookOpen size={24} weight="duotone" />
                    </Box>
                    <Typography sx={{
                        fontSize: '0.82rem', color: 'text.tertiary', lineHeight: 1.65,
                        letterSpacing: '-0.005em',
                    }}>
                        Читайте каждый модуль вдумчиво. Глубокое понимание важнее скорости. Клинические задачи в конце каждого дня обязательны.
                    </Typography>
                </Box>
                <Box sx={{
                    display: 'flex', flexWrap: 'wrap', gap: 1,
                    pt: 2, borderTop: '1px solid',
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                }}>
                    {[
                        { value: module.duration_days, label: 'дней' },
                        { value: module.topics_count, label: 'модулей' },
                        { value: module.tests_count, label: 'мини-вопросов' },
                        { value: module.clinical_cases_count, label: 'клин. задач' },
                    ].map((stat, i) => stat.value > 0 && (
                        <Box key={i} component="span" sx={{
                            display: 'inline-flex', flexDirection: 'row', alignItems: 'baseline', gap: '5px',
                            px: 1.4, py: 0.5, borderRadius: '8px',
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)',
                            whiteSpace: 'nowrap',
                        }}>
                            <Box component="span" sx={{
                                fontSize: '0.88rem', fontWeight: 650, letterSpacing: '-0.02em',
                                color: (theme) => theme.palette.mode === 'dark' ? '#DA7756' : '#37352F',
                            }}>{stat.value}</Box>
                            <Box component="span" sx={{ fontSize: '0.78rem', fontWeight: 450, color: 'text.secondary' }}>{stat.label}</Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Stack spacing={0.5}>
                {module.days.map((day) => (
                    <DaySection key={day.id} day={day} isEnrolled={isEnrolled} isCurrent={day.id === currentDayId} token={token} onRefresh={fetchModule} navigate={navigate} taskLayout={taskLayout} user={user} roadmapId={roadmapId} moduleId={moduleId} enableTourTargets={enableTourTargets} freeMode={module.free_mode} />
                ))}
            </Stack>
        </Box>
    );
}


// ─── Roadmap Card (Grid tile) ───
function RoadmapCard({ roadmap, onSelect, onEnroll, enrollLoading, dataTourId }) {
    return (
        <Sheet
            variant="outlined"
            onClick={() => onSelect(roadmap.id)}
            {...(dataTourId && { 'data-tour-id': dataTourId })}
            sx={{
                p: 1.5, borderRadius: '10px', cursor: 'pointer',
                minWidth: 0, overflow: 'hidden',
                border: '1px solid', borderColor: roadmap.is_enrolled ? 'rgba(184,76,49,0.3)' : 'divider',
                bgcolor: roadmap.is_enrolled
                    ? (theme) => theme.palette.mode === 'dark' ? 'rgba(184,76,49,0.04)' : 'rgba(184,76,49,0.02)'
                    : 'background.surface',
                transition: 'all 0.15s',
                '&:hover': { borderColor: 'rgba(184,76,49,0.5)', transform: 'translateY(-1px)', boxShadow: 'sm' },
            }}
        >
            <Typography level="title-sm" sx={{ fontWeight: 700, mb: 0.25 }} noWrap>{roadmap.title}</Typography>
            {roadmap.description && (
                <Typography level="body-xs" noWrap sx={{ color: 'text.secondary', mb: 1, fontSize: '0.7rem' }}>{roadmap.description}</Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                <Chip size="sm" variant="outlined" color="neutral" sx={{ fontSize: '0.65rem' }}>{roadmap.modules_count} модулей</Chip>
                <Chip size="sm" variant="outlined" color="neutral" sx={{ fontSize: '0.65rem' }}>{roadmap.total_days} дн.</Chip>
                {roadmap.is_enrolled && (
                    <Chip size="sm" variant="soft" color="warning" sx={{ fontSize: '0.65rem' }}>{roadmap.overall_progress_percent}%</Chip>
                )}
            </Box>

            {roadmap.is_enrolled ? (
                <LinearProgress determinate value={roadmap.overall_progress_percent}
                    sx={{ '--LinearProgress-thickness': '3px', color: '#B84C31', borderRadius: '2px' }} />
            ) : (
                <Button
                    size="sm" fullWidth
                    onClick={(e) => { e.stopPropagation(); onEnroll(roadmap.id); }}
                    loading={enrollLoading === roadmap.id}
                    sx={{ bgcolor: '#B84C31', color: '#fff', '&:hover': { bgcolor: '#9A3F28' }, fontSize: '0.75rem', py: 0.5 }}
                >
                    <Play size={14} style={{ marginRight: 4 }} /> Начать
                </Button>
            )}
        </Sheet>
    );
}


// ─── Roadmap List Item (List view) ───
function RoadmapListItem({ roadmap, onSelect, onEnroll, enrollLoading, dataTourId }) {
    const [expanded, setExpanded] = useState(roadmap.is_enrolled);

    return (
        <Sheet
            variant="outlined"
            {...(dataTourId && { 'data-tour-id': dataTourId })}
            sx={{ borderRadius: '12px', overflow: 'hidden', minWidth: 0 }}>
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: 2,
                    cursor: 'pointer', transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' },
                }}
            >
                {roadmap.is_enrolled ? (
                    <Circle size={22} color={STATE.active} />
                ) : (
                    <Lock size={20} color={STATE.locked} />
                )}

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="title-sm" sx={{ fontWeight: 600 }} noWrap>{roadmap.title}</Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }} noWrap>
                        {roadmap.modules_count} модулей · {roadmap.total_days} дн.
                        {roadmap.is_enrolled && ` · ${roadmap.overall_progress_percent}%`}
                    </Typography>
                </Box>

                {roadmap.is_enrolled ? (
                    <Box sx={{ width: 80 }}>
                        <LinearProgress determinate value={roadmap.overall_progress_percent}
                            sx={{ '--LinearProgress-thickness': '4px', color: '#B84C31', borderRadius: '2px' }} />
                    </Box>
                ) : (
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); onEnroll(roadmap.id); }}
                        loading={enrollLoading === roadmap.id}
                        sx={{ bgcolor: '#B84C31', color: '#fff', '&:hover': { bgcolor: '#9A3F28' }, fontSize: '0.75rem' }}>
                        Начать
                    </Button>
                )}

                <Box sx={{ color: 'text.tertiary', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'flex' }}>
                    <CaretDown size={18} />
                </Box>
            </Box>

            {expanded && (
                <Box sx={{ px: 2, pb: 2 }}>
                    <Stack spacing={1}>
                        {roadmap.modules.map((mod) => (
                            <Box
                                key={mod.id}
                                onClick={() => onSelect(roadmap.id, mod.id)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5,
                                    borderRadius: '8px', cursor: mod.status === 'locked' ? 'default' : 'pointer',
                                    opacity: mod.status === 'locked' ? 0.5 : 1,
                                    '&:hover': mod.status !== 'locked' ? {
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                                    } : {},
                                }}
                            >
                                {mod.status === 'completed' ? <CheckCircle size={18} color={STATE.completed} weight="fill" />
                                    : mod.status === 'locked' ? <Lock size={16} color={STATE.locked} />
                                    : <Circle size={18} color={STATE.active} />}
                                <Typography level="body-sm" sx={{ flex: 1, minWidth: 0, fontWeight: 500 }} noWrap>{mod.title}</Typography>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', flexShrink: 0 }}>
                                    {mod.total_days} дн.
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}
        </Sheet>
    );
}


// ─── Module Grid Tile (compact) ───
function ModuleGridTile({ mod, isCurrent, onSelect, index, user, token, onToggle, dataTourId }) {
    const { openUpgradeModal } = useContext(LayoutContext);
    const [toggling, setToggling] = useState(false);
    const isLocked = mod.status === 'locked';
    const isCompleted = mod.status === 'completed';
    const isAdmin = user?.role === 'admin';
    const isTrialLocked = mod.trial_locked === true;

    const handleToggle = async (e) => {
        e.stopPropagation();
        if (toggling) return;
        setToggling(true);
        try {
            await APITogglePlanModule(token, mod.id);
            onToggle?.();
        } catch (err) { console.error(err); }
        finally { setToggling(false); }
    };

    // Правило "не оставлять empty state": trial-locked модуль кликается и
    // открывает UpgradeModal. Prereq-lock (день ещё не наступил) остаётся
    // неактивным, т.к. там юзер просто не дошёл.
    const handleClick = () => {
        if (isTrialLocked) {
            openUpgradeModal(`«${mod.title}» откроется после оформления полного доступа.`);
            return;
        }
        if (!isLocked) onSelect(mod.id);
    };

    return (
        <Box
            onClick={handleClick}
            {...(dataTourId && { 'data-tour-id': dataTourId })}
            sx={{
                p: 1.5, borderRadius: '10px',
                cursor: isTrialLocked ? 'pointer' : (isLocked ? 'default' : 'pointer'),
                minWidth: 0, overflow: 'hidden',
                opacity: isTrialLocked ? 0.72 : (isLocked ? 0.5 : 1),
                bgcolor: (theme) => isTrialLocked
                    ? 'rgba(184, 76, 49, 0.04)'
                    : isCompleted
                        ? (theme.palette.mode === 'dark' ? '#252820' : '#f4f5f0')
                        : (theme.palette.mode === 'dark' ? '#2f2f2f' : '#FAF9F5'),
                border: '1px solid',
                borderColor: (theme) => isTrialLocked
                    ? 'rgba(184, 76, 49, 0.4)'
                    : isCompleted
                        ? (theme.palette.mode === 'dark' ? '#4a5038' : '#c5ccb0')
                        : (theme.palette.mode === 'dark' ? '#3a3a3a' : '#e0e0e0'),
                transition: 'all 0.15s',
                '&:hover': (isTrialLocked || !isLocked) ? {
                    transform: 'translateY(-1px)',
                    boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)',
                    borderColor: (theme) => isTrialLocked
                        ? '#B84C31'
                        : isCompleted
                            ? (theme.palette.mode === 'dark' ? '#5a6a48' : '#a8b892')
                            : (theme.palette.mode === 'dark' ? '#4a4a4a' : '#c0c0c0'),
                } : {},
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {/* Custom circle indicator — clickable for admin (or lock for trial) */}
                {isTrialLocked ? (
                    <Box sx={{
                        width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Lock size={14} weight="fill" color="#B84C31" />
                    </Box>
                ) : (
                    <Box
                        onClick={isAdmin ? handleToggle : undefined}
                        sx={{
                            width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                            border: isCompleted ? '2px solid #788C5D' : '2px solid #666',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: isAdmin ? 'pointer' : 'default',
                            transition: 'border-color 0.15s, transform 0.1s',
                            '&:hover': isAdmin ? { borderColor: isCompleted ? '#96a87e' : '#999', transform: 'scale(1.15)' } : {},
                        }}
                    >
                        {isCompleted && !toggling && <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#788C5D' }} />}
                        {toggling && <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px' }} />}
                    </Box>
                )}
                <Typography level="body-sm" noWrap sx={{
                    fontWeight: 600, flex: 1,
                    color: isTrialLocked
                        ? 'text.secondary'
                        : isCompleted ? '#788C5D' : isLocked ? 'text.tertiary' : 'text.primary',
                    textDecoration: isCompleted ? 'line-through' : 'none',
                    textDecorationColor: isCompleted ? '#a8b892' : undefined,
                }}>
                    {index}. {mod.title}
                </Typography>
                {isAdmin && mod.is_restructured && (
                    <Box sx={{ px: 0.5, py: 0.15, borderRadius: '3px', bgcolor: 'rgba(42,149,120,0.15)', border: '1px solid rgba(42,149,120,0.35)', flexShrink: 0 }}>
                        <Typography level="body-xs" sx={{ color: '#2a9578', fontWeight: 700, fontSize: '0.55rem' }}>✓</Typography>
                    </Box>
                )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                <Typography level="body-xs" sx={{
                    color: isTrialLocked ? 'text.tertiary' : (isCompleted ? '#788C5D' : '#D97757'),
                    fontWeight: 500, fontSize: '0.65rem',
                }}>
                    {mod.total_days} дн.
                </Typography>
                {isTrialLocked && (
                    <Typography level="body-xs" sx={{ color: '#B84C31', fontSize: '0.65rem', fontWeight: 600 }}>
                        · Открыть полный доступ
                    </Typography>
                )}
                {!isTrialLocked && isCompleted && (
                    <Typography level="body-xs" sx={{ color: '#788C5D', fontSize: '0.65rem' }}>
                        · Пройден
                    </Typography>
                )}
                {!isTrialLocked && !isCompleted && !isLocked && (
                    <Typography level="body-xs" sx={{ color: '#D97757', fontSize: '0.65rem' }}>
                        · {mod.completed_days}/{mod.total_days}
                    </Typography>
                )}
            </Box>
            {!isLocked && !isTrialLocked && (
                <LinearProgress determinate value={mod.progress_percent}
                    sx={{ '--LinearProgress-thickness': '5px', color: isCompleted ? '#788C5D' : '#D97757', borderRadius: '3px', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)' }} />
            )}
        </Box>
    );
}


// ─── Roadmap Modules View (Level 2) ───
function RoadmapModulesView({ roadmap, token, onBack, onSelectModule, user, onRefresh }) {
    const [moduleView, setModuleView] = useState(() => localStorage.getItem('plans_module_view') || 'grid');

    const toggleModuleView = (mode) => {
        setModuleView(mode);
        localStorage.setItem('plans_module_view', mode);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <IconButton variant="plain" onClick={onBack} size="sm"><ArrowLeft size={18} /></IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography level="h4" sx={{ fontWeight: 700 }}>{roadmap.title}</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        {roadmap.modules_count} модулей · {roadmap.total_days} дней
                        {roadmap.is_enrolled && ` · ${roadmap.overall_progress_percent}%`}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    <IconButton size="sm" variant={moduleView === 'grid' ? 'soft' : 'plain'} color={moduleView === 'grid' ? 'primary' : 'neutral'}
                        onClick={() => toggleModuleView('grid')} title="Плитки">
                        <SquaresFour size={18} />
                    </IconButton>
                    <IconButton size="sm" variant={moduleView === 'list' ? 'soft' : 'plain'} color={moduleView === 'list' ? 'primary' : 'neutral'}
                        onClick={() => toggleModuleView('list')} title="Список">
                        <List size={18} />
                    </IconButton>
                </Box>
            </Box>

            {roadmap.is_enrolled && (
                <LinearProgress determinate value={roadmap.overall_progress_percent}
                    sx={{ mb: 3, '--LinearProgress-thickness': '6px', color: '#B84C31', borderRadius: '3px' }} />
            )}

            {moduleView === 'grid' ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
                    {roadmap.modules.map((mod, idx) => (
                        <ModuleGridTile
                            key={mod.id}
                            mod={mod}
                            index={idx + 1}
                            isCurrent={roadmap.current_module_id === mod.id}
                            onSelect={onSelectModule}
                            user={user}
                            token={token}
                            onToggle={onRefresh}
                            dataTourId={idx === 0 ? 'module-card' : undefined}
                        />
                    ))}
                </Box>
            ) : (
                <Stack spacing={1.5}>
                    {roadmap.modules.map((mod, idx) => (
                        <ModuleCard
                            key={mod.id}
                            mod={mod}
                            index={idx + 1}
                            isCurrent={roadmap.current_module_id === mod.id}
                            onSelect={onSelectModule}
                            user={user}
                            token={token}
                            onToggle={onRefresh}
                            dataTourId={idx === 0 ? 'module-card' : undefined}
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
}


// ─── Main PlansPage ───
export default function PlansPage() {
    const { token, user } = useContext(LayoutContext);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [roadmaps, setRoadmaps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [enrollLoading, setEnrollLoading] = useState(null);

    // Restore plan state from URL params (when returning from module)
    const initRoadmapId = searchParams.get('roadmapId');
    const initRmId = searchParams.get('rmId');

    // Navigation state
    const [selectedRoadmapId, setSelectedRoadmapId] = useState(initRoadmapId ? Number(initRoadmapId) : null);
    const [selectedModuleId, setSelectedModuleId] = useState(initRmId ? Number(initRmId) : null);

    // Clear search params after reading (keep URL clean)
    useEffect(() => {
        if (initRoadmapId || initRmId) {
            setSearchParams({}, { replace: true });
        }
    }, []);

    // ─── Guided tour state ───
    // Triggered by WelcomeModal which sets localStorage['apex_tour_pending']='1'
    // before navigating here. Resumes mid-tour via localStorage['apex_tour_step']
    // (bumped whenever the user clicks a spotlighted element that navigates away).
    const [demoExplainerOpen, setDemoExplainerOpen] = useState(false);
    const [tourOpen, setTourOpen] = useState(false);
    const [tourFinishedToast, setTourFinishedToast] = useState(false);

    useEffect(() => {
        // Wait for the profile before deciding — otherwise a paid user can flash
        // the demo onboarding on first mount before `user` loads.
        if (!user) return;
        // Paid users have full access; the demo/trial onboarding ("Ты в демо-режиме")
        // must never show for them. Also clear any stale tour flags so an
        // unfinished tour from before they paid can't keep re-triggering.
        if (user.has_active_subscription) {
            localStorage.setItem(LS_KEYS.done, '1');
            return;
        }
        const pending = localStorage.getItem(LS_KEYS.pending);
        const done = localStorage.getItem(LS_KEYS.done);
        const step = localStorage.getItem(LS_KEYS.step);
        if (pending === '1' && done !== '1') {
            if (step == null) {
                // Fresh tour (just came from WelcomeModal) → show explainer first.
                setDemoExplainerOpen(true);
            } else {
                // Resuming mid-tour (return from module/test-session page).
                setTourOpen(true);
            }
        }
    }, [user]);

    const handleExplainerStart = () => {
        // Start tour at the step that matches whichever level the user is
        // already on. Enrolled users get auto-selected past Level 1
        // (roadmap-card), so step 0's target wouldn't exist and the tour
        // would spend TARGET_MISSING_TIMEOUT on a dim screen before recovering.
        let startStep = 0;
        if (selectedModuleId) {
            startStep = 2; // Level 3 — first-read-task
        } else if (selectedRoadmapId) {
            startStep = 1; // Level 2 — module-card
        }
        localStorage.setItem(LS_KEYS.step, String(startStep));
        setDemoExplainerOpen(false);
        setTourOpen(true);
    };

    const handleTourFinish = () => {
        setTourOpen(false);
        setTourFinishedToast(true);
        setTimeout(() => setTourFinishedToast(false), 4200);
    };

    const handleRestartTour = () => {
        localStorage.setItem(LS_KEYS.pending, '1');
        localStorage.removeItem(LS_KEYS.done);
        localStorage.removeItem(LS_KEYS.step);
        setDemoExplainerOpen(true);
    };

    const tourActive = demoExplainerOpen || tourOpen;

    // View toggle
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('plans_view_mode') || 'grid');

    const toggleView = (mode) => {
        setViewMode(mode);
        localStorage.setItem('plans_view_mode', mode);
    };

    const fetchRoadmaps = useCallback(async () => {
        try {
            setLoading(true);
            const res = await APIGetRoadmaps(token);
            if (res.success) {
                setRoadmaps(res.roadmaps);

                // Auto-select the first enrolled roadmap (works with multiple roadmaps)
                if (!selectedRoadmapId) {
                    const enrolled = res.roadmaps.find(r => r.is_enrolled);
                    if (enrolled) setSelectedRoadmapId(enrolled.id);
                }
            }
        } catch (e) {
            setError('Не удалось загрузить планы');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchRoadmaps(); }, [fetchRoadmaps]);

    const handleEnroll = async (roadmapId) => {
        setEnrollLoading(roadmapId);
        try {
            const res = await APIEnrollRoadmap(token, roadmapId);
            if (res.success) {
                await fetchRoadmaps();
                setSelectedRoadmapId(roadmapId);
            }
        } catch (e) {
            setError('Ошибка записи');
        } finally {
            setEnrollLoading(null);
        }
    };

    const pageBg = (theme) => theme.palette.mode === 'dark' ? 'transparent' : '#F0EEE7';
    const contentSx = { width: '100%', maxWidth: 800, mx: 'auto', py: 4, px: 2, boxSizing: 'border-box', overflowX: 'hidden' };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    if (roadmaps.length === 0) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
                <Box sx={contentSx}>
                    <Typography level="h3" sx={{ fontWeight: 700, mb: 2 }}>Study Plan</Typography>
                    <Sheet variant="outlined" sx={{ p: 4, borderRadius: '16px', textAlign: 'center' }}>
                        <Typography level="body-lg" sx={{ color: 'text.secondary' }}>Планы пока не опубликованы</Typography>
                    </Sheet>
                </Box>
            </Box>
        );
    }

    const selectedRoadmap = roadmaps.find(r => r.id === selectedRoadmapId);

    // Tour chrome — rendered alongside whichever level is active.
    const tourChrome = (
        <>
            <DemoExplainerModal open={demoExplainerOpen} onStart={handleExplainerStart} />
            <OnboardingTour open={tourOpen} onFinish={handleTourFinish} />
            <AnimatePresence>
                {tourFinishedToast && (
                    <motion.div
                        key="tour-finished-toast"
                        initial={{ opacity: 0, y: -16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -16 }}
                        transition={{ duration: 0.28 }}
                        style={{
                            position: 'fixed',
                            top: 24,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 10050,
                        }}
                    >
                        <Box sx={(theme) => ({
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            bgcolor: theme.palette.mode === 'dark' ? '#2B2B2B' : '#FFFFFF',
                            px: 2.5,
                            py: 1.5,
                            borderRadius: 'xl',
                            boxShadow: '0 12px 40px rgba(0,0,0,0.2), 0 0 0 1px rgba(218,119,86,0.25)',
                            border: '1.5px solid #DA7756',
                        })}>
                            <Confetti size={22} color="#DA7756" weight="duotone" />
                            <Box>
                                <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                                    Ты прошёл тур!
                                </Typography>
                                <Typography level="body-xs" color="neutral">
                                    Теперь доверяй плану и не сливайся.
                                </Typography>
                            </Box>
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );

    // Level 3: Module detail (days/tasks)
    if (selectedModuleId) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
                <Box sx={contentSx}>
                    <ModuleDetailView
                        moduleId={selectedModuleId}
                        roadmapId={selectedRoadmapId}
                        token={token}
                        onBack={() => { setSelectedModuleId(null); fetchRoadmaps(); }}
                        navigate={navigate}
                        user={user}
                        enableTourTargets={tourActive}
                        onRestartTour={handleRestartTour}
                    />
                </Box>
                {tourChrome}
            </Box>
        );
    }

    // Level 2: Modules inside a roadmap
    if (selectedRoadmap) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
                <Box sx={contentSx}>
                    {user?.has_active_subscription && <AccessInfoBanner sx={{ mb: 2.5 }} />}
                    <RoadmapModulesView
                        roadmap={selectedRoadmap}
                        token={token}
                        onBack={() => { setSelectedRoadmapId(null); fetchRoadmaps(); }}
                        onSelectModule={setSelectedModuleId}
                        user={user}
                        onRefresh={fetchRoadmaps}
                    />
                </Box>
                {tourChrome}
            </Box>
        );
    }

    // Level 1: All roadmaps (grid or list)
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
        <Box sx={contentSx}>
            {user?.has_active_subscription && <AccessInfoBanner sx={{ mb: 2.5 }} />}
            {/* Header with toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="h3" sx={{ fontWeight: 700 }}>Study Plan</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {roadmaps.length} планов
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    <IconButton
                        size="sm"
                        variant={viewMode === 'grid' ? 'soft' : 'plain'}
                        color={viewMode === 'grid' ? 'primary' : 'neutral'}
                        onClick={() => toggleView('grid')}
                    >
                        <SquaresFour size={20} />
                    </IconButton>
                    <IconButton
                        size="sm"
                        variant={viewMode === 'list' ? 'soft' : 'plain'}
                        color={viewMode === 'list' ? 'primary' : 'neutral'}
                        onClick={() => toggleView('list')}
                    >
                        <List size={20} />
                    </IconButton>
                </Box>
            </Box>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            {viewMode === 'grid' ? (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1.5,
                    width: '100%',
                    minWidth: 0,
                }}>
                    {roadmaps.map((r, idx) => (
                        <RoadmapCard
                            key={r.id}
                            roadmap={r}
                            onSelect={setSelectedRoadmapId}
                            onEnroll={handleEnroll}
                            enrollLoading={enrollLoading}
                            dataTourId={idx === 0 ? 'roadmap-card' : undefined}
                        />
                    ))}
                </Box>
            ) : (
                <Stack spacing={1.5} sx={{ width: '100%', minWidth: 0 }}>
                    {roadmaps.map((r, idx) => (
                        <RoadmapListItem
                            key={r.id}
                            roadmap={r}
                            onSelect={(roadmapId, moduleId) => {
                                setSelectedRoadmapId(roadmapId);
                                if (moduleId) setSelectedModuleId(moduleId);
                            }}
                            onEnroll={handleEnroll}
                            enrollLoading={enrollLoading}
                            dataTourId={idx === 0 ? 'roadmap-card' : undefined}
                        />
                    ))}
                </Stack>
            )}
        </Box>
        {tourChrome}
        </Box>
    );
}
